'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';
import {
    usePathname as useAppRouterPathname,
    useRouter as useAppRouter,
    useSearchParams
} from 'next/navigation';
import type { NextRouter } from 'next/router';
import { useRouter as usePageRouter } from 'next/router';

import { APP_ROUTER_ROUTES, stripLocalePrefix } from '@/lib/routes/appRoutes';

type UnifiedTransitionOptions = Parameters<NextRouter['push']>[2];

/**
 * Unified router interface that works with both App Router and Pages Router
 */
export type UnifiedRouter = Pick<
    NextRouter,
    | 'pathname'
    | 'asPath'
    | 'query'
    | 'back'
    | 'locale'
    | 'locales'
    | 'defaultLocale'
    | 'route'
    | 'isFallback'
    | 'events'
    | 'reload'
    | 'beforePopState'
    | 'isReady'
    | 'isPreview'
> & {
    push: (url: string, as?: string, options?: UnifiedTransitionOptions) => void | Promise<boolean>;
    replace: (
        url: string,
        as?: string,
        options?: UnifiedTransitionOptions
    ) => void | Promise<boolean>;
    prefetch: (url: string) => void | Promise<void>;
};

/**
 * Check if a route should be handled by the App Router
 * Strips locale prefix for comparison
 */
function isAppRouterRoute(pathname: string): boolean {
    if (!pathname) return false;
    return APP_ROUTER_ROUTES.some((route) => stripLocalePrefix(pathname) === route);
}

/**
 * Convert URLSearchParams to query object matching Pages Router format
 */
function searchParamsToQuery(
    searchParams: URLSearchParams | null
): Record<string, string | string[] | undefined> {
    if (!searchParams) {
        return {};
    }

    const query: Record<string, string | string[] | undefined> = {};

    searchParams.forEach((value, key) => {
        const existing = query[key];
        if (existing !== undefined) {
            // If key already exists, convert to array
            query[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
        } else {
            query[key] = value;
        }
    });

    return query;
}

/**
 * Build asPath from pathname and search params
 */
function buildAsPath(pathname: string, searchParams: URLSearchParams | null): string {
    if (!searchParams || !searchParams.toString()) {
        return pathname;
    }
    return `${pathname}?${searchParams.toString()}`;
}

// Context for Pages Router (only used in Pages Router context)
const PagesRouterContext = createContext<NextRouter | null>(null);

/**
 * Provider for Pages Router context
 * Wrap your Pages Router pages with this to enable useNewRouter
 */
export function PagesRouterProvider({ children }: { children: ReactNode }) {
    const pageRouter = usePageRouter();
    return <PagesRouterContext.Provider value={pageRouter}>{children}</PagesRouterContext.Provider>;
}

/**
 * Compatibility hook for router navigation.
 * Works with both App Router (next/navigation) and Pages Router (next/router).
 *
 * This hook provides a unified interface that includes all commonly used router properties:
 * - Navigation methods: push, replace, back, prefetch
 * - Route information: pathname, asPath, query
 * - Locale information: locale, locales, defaultLocale (Pages Router only)
 *
 * The hook automatically detects which router system is active and provides
 * the appropriate implementation:
 * - For App Router pages: Uses hooks from next/navigation
 * - For Pages Router pages: Uses Pages Router from context (requires PagesRouterProvider)
 *
 * @returns Unified router object with all navigation methods and route information
 *
 * @example
 * ```tsx
 * const router = useRouter();
 *
 * // Navigation methods (work in both routers)
 * router.push('/posts/123');
 * router.replace('/home');
 * router.back();
 *
 * // Route information (work in both routers)
 * console.log(router.pathname); // Current pathname
 * console.log(router.query);    // Query parameters as object
 * console.log(router.asPath);   // Full path with query string
 *
 * // Pages Router specific properties (undefined in App Router)
 * console.log(router.locale);   // Current locale
 * console.log(router.events);   // Router events
 * ```
 */
export const useRouter = (): UnifiedRouter => {
    // ALWAYS call App Router hooks (they're safe in both contexts)
    const appRouterPathname = useAppRouterPathname();
    const appRouter = useAppRouter();
    const searchParams = useSearchParams();

    // Get Pages Router from context (only available if wrapped with PagesRouterProvider)
    const pageRouter = useContext(PagesRouterContext);

    // Detect which context we're in
    // App Router hooks return actual values in App Router, null in Pages Router
    const isInAppRouterContext = isAppRouterRoute(appRouterPathname);

    // Memoize the unified router object
    const unifiedRouter = useMemo(() => {
        if (isInAppRouterContext) {
            // App Router mode: Build compatibility layer
            const query = searchParamsToQuery(searchParams);
            const asPath = buildAsPath(appRouterPathname, searchParams);

            return {
                // Navigation methods (App Router)
                push: (url: string, _as?: string, options?: any) => {
                    appRouter.push(url, options);
                },
                replace: (url: string, _as?: string, options?: any) => {
                    appRouter.replace(url, options);
                },
                back: () => appRouter.back(),
                prefetch: (url: string) => appRouter.prefetch(url),
                refresh: () => appRouter.refresh(),

                // Route information (from hooks)
                pathname: appRouterPathname,
                asPath,
                query,

                // Pages Router properties (undefined in App Router)
                locale: undefined,
                locales: undefined,
                defaultLocale: undefined,
                route: appRouterPathname,
                isFallback: false,
                events: undefined,
                reload: undefined,
                beforePopState: undefined,
                isReady: true,
                isPreview: false
            } as UnifiedRouter;
        } else if (pageRouter) {
            // Pages Router mode: Use native router from context
            return {
                // Navigation methods (Pages Router)
                push: pageRouter.push.bind(pageRouter),
                replace: pageRouter.replace.bind(pageRouter),
                back: pageRouter.back.bind(pageRouter),
                prefetch: pageRouter.prefetch.bind(pageRouter),

                // Route information (from Pages Router)
                pathname: pageRouter.pathname,
                asPath: pageRouter.asPath,
                query: pageRouter.query,

                // Pages Router specific properties
                locale: pageRouter.locale,
                locales: pageRouter.locales,
                defaultLocale: pageRouter.defaultLocale,
                route: pageRouter.route,
                isFallback: pageRouter.isFallback,
                events: pageRouter.events,
                reload: pageRouter.reload?.bind(pageRouter),
                beforePopState: pageRouter.beforePopState?.bind(pageRouter),
                isReady: pageRouter.isReady,
                isPreview: pageRouter.isPreview
            } as UnifiedRouter;
        } else {
            throw new Error(
                '[useNewRouter]: Cannot detect router context. ' +
                    'If using in Pages Router, wrap your app with <PagesRouterProvider>.'
            );
        }
    }, [isInAppRouterContext, appRouterPathname, appRouter, searchParams, pageRouter]);

    return unifiedRouter;
};
